import { useCallback, useState } from "react";
import { styled } from "styled-components";
import { TrashIcon } from "@heroicons/react/24/outline";
import { HtmlContent } from "../../components";
import { useCart } from "../../contexts";
import { cancelOrder } from "../../services/orderService";
import { createAuctionPlaceholderImage } from "../../lib/auctionPresentation";
import { OutlineOrangeButton, StyledButton } from "../../components/Typography";

const StepHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
`;

const StepEyebrow = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
`;

const StepTitle = styled.h2`
  margin: 0;
  font-family: "Sofia Pro", sans-serif;
  font-size: 30px;
  line-height: 1.1;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue900};

  @media (max-width: 767px) {
    font-size: 24px;
  }
`;

const StepText = styled.p`
  margin: 0;
  max-width: 720px;
  font-family: "Sofia Pro", sans-serif;
  font-size: 15px;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.blue500};
`;

const ItemCard = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 320px) minmax(0, 1fr);
  gap: 24px;
  padding: 22px;
  border-radius: 24px;
  background:
    linear-gradient(180deg, rgba(246, 163, 28, 0.07) 0%, rgba(246, 163, 28, 0) 100%),
    ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 767px) {
    padding: 18px;
    border-radius: 20px;
    gap: 18px;
  }
`;

const ItemImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 20px;
  background: ${({ theme }) => theme.colors.grey100};
`;

const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const InfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ItemBadge = styled.div`
  width: fit-content;
  padding: 7px 12px;
  border-radius: 999px;
  font-family: "Sofia Pro", sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue500};
  background: ${({ theme }) => `${theme.colors.primary}20`};
`;

const ItemTitle = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 28px;
  line-height: 1.15;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue900};

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const ItemDescription = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 15px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.blue500};

  ul {
    margin: 0;
    padding-left: 18px;
  }

  li + li {
    margin-top: 6px;
  }
`;

const HelperPanel = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const HelperCard = styled.div`
  padding: 16px 18px;
  border-radius: 18px;
  background: ${({ theme }) => theme.colors.grey100};
  font-family: "Sofia Pro", sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.blue500};
`;

const HelperTitle = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue900};
  margin-bottom: 4px;
`;

const RemoveButton = styled(OutlineOrangeButton)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
`;

const ConfirmOverlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  display: ${({ $open }) => ($open ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(10, 17, 41, 0.52);
  z-index: 40;
`;

const ConfirmCard = styled.div`
  width: min(460px, 100%);
  padding: 24px;
  border-radius: 24px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0 20px 48px rgba(13, 18, 45, 0.24);
`;

const ConfirmTitle = styled.h3`
  margin: 0 0 10px;
  font-family: "Sofia Pro", sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue900};
`;

const ConfirmText = styled.p`
  margin: 0;
  font-family: "Sofia Pro", sans-serif;
  font-size: 15px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.blue500};
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;

  @media (max-width: 767px) {
    flex-direction: column-reverse;
  }
`;

const First = () => {
  const { cartProduct: product, clearCart, orderId } = useCart();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const imageFallback = createAuctionPlaceholderImage(product?.title || "Produkt");

  const removeProduct = useCallback(async () => {
    if (orderId) {
      try {
        await cancelOrder(orderId);
      } catch {
        // Local reset should still happen even if the cancellation call fails.
      }
    }

    clearCart();
  }, [clearCart, orderId]);

  if (!product) {
    return null;
  }

  return (
    <>
      <StepHeader>
        <StepEyebrow>Krok 1</StepEyebrow>
        <StepTitle>Zkontrolujte položku v košíku</StepTitle>
        <StepText>
          Ještě před vyplněním údajů si potvrďte, že pokračujete se správnou
          výhrou. Na dalším kroku už jen doplníte kontakt a fakturační údaje.
        </StepText>
      </StepHeader>

      <ItemCard>
        <ItemImage
          src={product.photoUrls?.[0] || imageFallback}
          alt={product.title}
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = imageFallback;
          }}
        />

        <ItemContent>
          <MetaRow>
            <InfoColumn>
              <ItemBadge>1 položka v košíku</ItemBadge>
              <ItemTitle>{product.title}</ItemTitle>
            </InfoColumn>
            <RemoveButton onClick={() => setConfirmOpen(true)}>
              <TrashIcon style={{ width: 18, height: 18 }} />
              Odstranit z košíku
            </RemoveButton>
          </MetaRow>

          {product.description && (
            <ItemDescription>
              <HtmlContent html={product.description} />
            </ItemDescription>
          )}

          <HelperPanel>
            <HelperCard>
              <HelperTitle>Co bude dál</HelperTitle>
              Vyplníte kontaktní údaje, zkontrolujete shrnutí a pokračujete na platbu.
            </HelperCard>
            <HelperCard>
              <HelperTitle>Proč je to přehlednější</HelperTitle>
              Checkout jsme rozdělili do čtyř krátkých kroků, takže vždy víte, kde právě jste.
            </HelperCard>
          </HelperPanel>
        </ItemContent>
      </ItemCard>

      <ConfirmOverlay $open={confirmOpen}>
        <ConfirmCard>
          <ConfirmTitle>Odstranit položku z košíku?</ConfirmTitle>
          <ConfirmText>
            Košík se vyprázdní a případná rozpracovaná objednávka se lokálně zruší.
          </ConfirmText>
          <ConfirmActions>
            <OutlineOrangeButton onClick={() => setConfirmOpen(false)}>
              Zpět
            </OutlineOrangeButton>
            <StyledButton onClick={removeProduct}>Ano, odstranit</StyledButton>
          </ConfirmActions>
        </ConfirmCard>
      </ConfirmOverlay>
    </>
  );
};

export default First;
